import { useState } from "react";
import instance from "../../index";
import { PagedList } from "../../../models/paged-list";
import { BaseResponse } from "../../../models/base-response";
import {
    CreateJustificationCommand,
    UpdateJustificationCommand
} from "../../../models/requests/create-justification-command";
import {JustificationDto} from "../../../models/requests/justification-dto";
import {JustificationFilters} from "../../../models/requests/justification-filters";

const useJustificationService = () => {
    const [loading, setLoading] = useState<boolean>(false);

    const handleRequest = async <T>(request: Promise<T>): Promise<T> => {
        setLoading(true);
        try {
            return await request;
        } finally {
            setLoading(false);
        }
    };

    async function createJustification(command: CreateJustificationCommand): Promise<JustificationDto> {
        return handleRequest(instance.post<JustificationDto>('/v1/backoffice/justifications', command).then(res => res.data));
    }

    async function activateJustification(id: string): Promise<JustificationDto> {
        return handleRequest(instance.put<JustificationDto>(`/v1/backoffice/justifications/${id}/activate`).then(res => res.data));
    }

    async function deactivateJustification(id: string): Promise<JustificationDto> {
        return handleRequest(instance.put<JustificationDto>(`/v1/backoffice/justifications/${id}/deactivate`).then(res => res.data));
    }

    async function getJustifications(filter?: JustificationFilters): Promise<PagedList<JustificationDto>> {
        return handleRequest(instance.get<BaseResponse<PagedList<JustificationDto>>>("/v1/backoffice/justifications", { params: filter })
            .then(res => res.data.data));
    }

    async function getJustificationById(id: string): Promise<JustificationDto> {
        return handleRequest(instance.get<BaseResponse<JustificationDto>>(`/v1/backoffice/justifications/${id}`).then(res => res.data.data));
    }

    async function updateJustification(id: string, command: UpdateJustificationCommand): Promise<JustificationDto> {
        return handleRequest(instance.put<JustificationDto>(`/v1/backoffice/justifications/${id}`, command).then(res => res.data));
    }

    async function deleteJustification(id: string): Promise<void> {
        return handleRequest(instance.delete(`/v1/backoffice/justifications/${id}`).then(() => {}));
    }

    async function getJustificationsShort(): Promise<JustificationDto[]> {
        return handleRequest(instance.get<BaseResponse<JustificationDto[]>>(`/v1/backoffice/justifications/short`).then(res => res.data.data));
    }

    return {
        loading,
        createJustification,
        activateJustification,
        deactivateJustification,
        getJustifications,
        getJustificationById,
        updateJustification,
        deleteJustification,
        getJustificationsShort
    };
};

export default useJustificationService;
